import { useCallback, useEffect, useRef, useState } from "react";
import { useTableContext } from "./useTableContext";

export const useControlPanelHover = (
  functionBarRef,
  setControlsPosition,
  setLastValidHoveredColumn,
  tableContainerRef
) => {
  const isResizing = useRef(false);
  const [isPanelSticky, setIsPanelSticky] = useState(false);
  const [isPanelHovered, setIsPanelHovered] = useState(false);

  // Add refs for timeouts to prevent conflicts
  const hoverTimeoutRef = useRef(null);
  const stickyTimeoutRef = useRef(null);

  const { setHoveredColumn, state } = useTableContext();
  const { hoveredColumn } = state;

  // Fix: Update the ref whenever state changes
  const latestHoverStateRef = useRef();
  useEffect(() => {
    latestHoverStateRef.current = {
      hoveredColumn,
      isPanelHovered,
      isPanelSticky,
    };
  }, [hoveredColumn, isPanelHovered, isPanelSticky]);

  // Update last valid hovered column
  useEffect(() => {
    if (hoveredColumn) {
      setLastValidHoveredColumn(hoveredColumn);
    }
  }, [hoveredColumn, setLastValidHoveredColumn]);

  // Clear any pending timeouts
  const clearAllTimeouts = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (stickyTimeoutRef.current) {
      clearTimeout(stickyTimeoutRef.current);
      stickyTimeoutRef.current = null;
    }
  }, []);

  // Improved header hover detection
  useEffect(() => {
    if (!tableContainerRef.current || !functionBarRef.current) return;

    // Handle table leave
    const handleTableLeave = () => {
      if (isResizing.current) return;

      // Clear any existing timeout
      clearAllTimeouts();

      // Only clear hover if panel is not hovered or sticky
      if (!isPanelHovered && !isPanelSticky) {
        hoverTimeoutRef.current = setTimeout(() => {
          setHoveredColumn(null);
        }, 150);
      }
    };

    // Set up table leave listener
    const tableElement = tableContainerRef.current.querySelector("table");
    if (tableElement) {
      tableElement.addEventListener("mouseleave", handleTableLeave);
    }

    // Clean up
    return () => {
      if (tableElement) {
        tableElement.removeEventListener("mouseleave", handleTableLeave);
      }
      clearAllTimeouts();
    };
  }, [isPanelHovered, isPanelSticky, setHoveredColumn, clearAllTimeouts]);

  const handleHeaderMouseEnter = useCallback(
    (e, columnId) => {
      if (isResizing.current) return;

      // Clear any pending hover clear timeouts
      clearAllTimeouts();

      const thRect = e.currentTarget.getBoundingClientRect();
      if (!functionBarRef.current) return;

      const barRect = functionBarRef.current.getBoundingClientRect();
      const centerX = thRect.left + thRect.width / 2 - barRect.left;

      setHoveredColumn(columnId);
      setLastValidHoveredColumn(columnId);
      setControlsPosition({ left: `${centerX}px` });
    },
    [
      setHoveredColumn,
      setLastValidHoveredColumn,
      setControlsPosition,
      clearAllTimeouts,
    ]
  );

  const handlePanelHover = useCallback(
    (isHovered) => {
      setIsPanelHovered(isHovered);

      // Clear any existing sticky timeout
      if (stickyTimeoutRef.current) {
        clearTimeout(stickyTimeoutRef.current);
        stickyTimeoutRef.current = null;
      }

      if (isHovered) {
        setIsPanelSticky(true);
      } else {
        // Delay before removing sticky state and clearing hover
        stickyTimeoutRef.current = setTimeout(() => {
          setIsPanelSticky(false);
          // Clear hover when leaving panel (unless hovering over header)
          setHoveredColumn(null);
        }, 150); // Increased timeout to prevent flicker
      }
    },
    [setHoveredColumn]
  );
  const handleHeaderMouseLeave = useCallback(
    (_, leavingColumnId) => {
      if (isResizing.current) return;

      // Clear any existing timeout
      clearAllTimeouts();

      hoverTimeoutRef.current = setTimeout(() => {
        const currentState = latestHoverStateRef.current;

        if (
          !currentState.isPanelHovered &&
          !currentState.isPanelSticky &&
          currentState.hoveredColumn === leavingColumnId
        ) {
          setHoveredColumn(null);
        }
      }, 50);
    },
    [setHoveredColumn, clearAllTimeouts]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  return {
    handleHeaderMouseEnter,
    handleHeaderMouseLeave,
    handlePanelHover,
    hoveredColumn,
  };
};
